import { useCallback, useEffect, useRef, useState } from 'react';
import { IconBrandTelegram, IconCircleCheck, IconRefresh } from '@tabler/icons-react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material';
import { getFreeStartStatus } from 'src/services/LeadStreamsService/freeStart';
import { confirmEmail, refreshEmailCode } from 'src/services/SessionService/agent';
import UserService from 'src/services/UserSevice';
import useUserStore from 'src/stores/user';

import type { FreeStartStatus } from 'src/services/LeadStreamsService/freeStart';

interface Props {
    niche: string;
    disabled: boolean;
    onActivate: () => Promise<void>;
    onAvailabilityChange?: (available: boolean) => void;
}

const failureMessage = (error: any, fallback: string) => {
    if ([404, 405].includes(error?.response?.status)) {
        return 'Бесплатный старт станет доступен после обновления сервера.';
    }
    const message = error?.response?.data?.error;
    return (Array.isArray(message) ? message[0] : message) || fallback;
};

const FREE_START_PRIMARY_SX = {
    bgcolor: '#0044FF',
    color: '#FFFFFF',
    border: '1px solid #0044FF',
    boxShadow: 'none',
    textTransform: 'none',
    minHeight: 48,
    borderRadius: '12px',
    fontWeight: 600,
    '&:hover': { bgcolor: '#0035D9', borderColor: '#0035D9', boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: '#DDE6F8', borderColor: '#DDE6F8', color: '#7386A6' },
} as const;

export default function FreeStart({ niche, disabled, onActivate, onAvailabilityChange }: Props) {
    const [status, setStatus] = useState<FreeStartStatus | null>(null);
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [code, setCode] = useState('');
    const [bindLink, setBindLink] = useState('');
    const email = useUserStore((state) => state.email);
    const mounted = useRef(false);
    const actionInProgress = useRef(false);

    const refresh = useCallback(async () => {
        try {
            const next = await getFreeStartStatus();
            if (mounted.current) {
                setStatus(next);
                setError('');
                onAvailabilityChange?.(next.available);
            }
        } catch (failure) {
            if (mounted.current) {
                setError(failureMessage(failure, 'Не удалось проверить бесплатный старт. Повторите попытку.'));
                setStatus(null);
                onAvailabilityChange?.(false);
            }
        } finally {
            if (mounted.current) setLoading(false);
        }
    }, [onAvailabilityChange]);

    useEffect(() => {
        mounted.current = true;
        void refresh();
        return () => {
            mounted.current = false;
        };
    }, [refresh]);

    useEffect(() => {
        if (!open || step !== 1) return;
        let live = true;
        if (!status?.telegram_connected)
            void UserService.getTelegramBindLink()
                .then((result) => {
                    if (live) setBindLink(result.telegram_bind_link);
                })
                .catch(() => {
                    if (live) setError('Не удалось получить ссылку на бота. Повторите попытку.');
                });
        const onFocus = () => {
            void refresh();
        };
        window.addEventListener('focus', onFocus);
        const timer = window.setInterval(onFocus, 5000);
        return () => {
            live = false;
            window.removeEventListener('focus', onFocus);
            window.clearInterval(timer);
        };
    }, [open, step, refresh, status?.telegram_connected]);

    const runAction = async (action: () => Promise<void>) => {
        if (actionInProgress.current) return;
        actionInProgress.current = true;
        setBusy(true);
        setError('');
        setNotice('');
        try {
            await action();
        } catch (failure) {
            if (mounted.current) setError(failureMessage(failure, 'Не удалось выполнить действие. Повторите попытку.'));
        } finally {
            actionInProgress.current = false;
            if (mounted.current) setBusy(false);
        }
    };
    const ready = Boolean(status?.available && status.email_confirmed && status.telegram_connected);
    const close = () => {
        if (!busy && !disabled) setOpen(false);
    };

    if (status && !status.available && !open) return null;

    return (
        <>
            <Box
                component="section"
                aria-labelledby="free-start-offer-title"
                sx={{
                    mb: 3,
                    p: { xs: 1.5, sm: 2 },
                    border: '1px solid #C9DAFF',
                    borderRadius: '8px',
                    bgcolor: '#F0F5FF',
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                        <Typography component="h3" id="free-start-offer-title" sx={{ fontSize: 17, fontWeight: 750, lineHeight: 1.3 }}>15 лидов в подарок</Typography>
                        <Typography sx={{ color: 'var(--lm-muted, #61718B)', fontSize: 13, mt: 0.5 }}>
                            Подтвердите почту и подключите Telegram-бота. Без карты и автопродления.
                        </Typography>
                    </Box>
                    <Button
                        type="button"
                        variant="contained"
                        sx={{ ...FREE_START_PRIMARY_SX, px: 2, minHeight: 44, flexShrink: 0 }}
                        disabled={disabled || loading || !status?.available}
                        onClick={() => {
                            setStep(0);
                            setOpen(true);
                            setNotice('');
                        }}
                    >
                        {loading ? 'Проверяем доступность…' : 'Получить 15 лидов бесплатно'}
                    </Button>
                </Stack>
                {error && !open && (
                    <Alert
                        severity="info"
                        sx={{ mt: 2 }}
                    >
                        {error}
                    </Alert>
                )}
                {!status && !loading && (
                    <Button
                        type="button"
                        sx={{ mt: 1 }}
                        onClick={() => void refresh()}
                    >
                        Повторить проверку
                    </Button>
                )}
            </Box>
            <Dialog
                open={open}
                onClose={close}
                fullWidth
                maxWidth="sm"
                aria-labelledby="free-start-title"
                PaperProps={{ sx: {
                    bgcolor: 'var(--lm-surface, #FFFFFF)', color: 'var(--lm-text, #172338)', borderRadius: '16px',
                    border: '1px solid var(--lm-border, #DDE6F0)', m: { xs: 2, sm: 3 },
                    width: { xs: 'calc(100% - 32px)', sm: '100%' }, maxHeight: 'calc(100dvh - 32px)',
                    '& .MuiTypography-root, & .MuiButton-root, & .MuiStepLabel-label': { fontFamily: '"Inter", system-ui, sans-serif' },
                    '& .MuiDialogContent-root': { px: { xs: 2, sm: 3 }, pb: 2 },
                    '& .MuiDialogContent-root .MuiTypography-body1, & .MuiDialogContent-root .MuiTypography-body2': { fontSize: '14px', lineHeight: 1.55 },
                    '& .MuiStepLabel-label': { fontSize: '12px !important', fontWeight: 600 },
                    '& .MuiButton-root': { fontSize: '14px !important', fontWeight: 600, textTransform: 'none', borderRadius: '8px', minHeight: 44 },
                } }}
            >
                <DialogTitle id="free-start-title" sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1.5, fontSize: { xs: '22px !important', sm: '24px !important' }, fontWeight: 750, lineHeight: 1.2 }}>15 лидов в подарок</DialogTitle>
                <DialogContent>
                    <Stepper
                        activeStep={step}
                        alternativeLabel
                        sx={{
                            mt: 1,
                            mb: 3,
                            '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: '#0044FF' },
                        }}
                    >
                        {['Условия', 'Привязка', 'Запуск'].map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    {error && (
                        <Alert
                            severity="error"
                            sx={{ mb: 2 }}
                        >
                            {error}
                        </Alert>
                    )}
                    {notice && (
                        <Alert
                            severity="success"
                            sx={{ mb: 2 }}
                        >
                            {notice}
                        </Alert>
                    )}
                    {status && !status.available && (
                        <Alert
                            severity="info"
                            sx={{ mb: 2 }}
                        >
                            Бесплатный старт уже использован.
                        </Alert>
                    )}
                    {step === 0 && (
                        <Stack spacing={1.5}>
                            <Box sx={{ p: 2, borderRadius: '8px', bgcolor: 'var(--lm-accent-soft, #EEF3FF)', border: '1px solid var(--lm-border, #DDE6F0)' }}>
                                <Typography sx={{ fontSize: '32px !important', fontWeight: 800, color: 'var(--lm-accent-ink, #0044FF)', lineHeight: 1.1 }}>15 целевых лидов</Typography>
                                <Typography sx={{ mt: 0.75, color: 'var(--lm-muted, #61718B)' }}>Для ниши «{niche}»</Typography>
                            </Box>
                            <Typography>Найдём заявки по правилам вашего потока. Подарочный пакет доступен один раз.</Typography>
                            <Typography color="text.secondary">После 15 лидов поток остановится, если нет оплаченных лидов или активного доступа.</Typography>
                            <Typography sx={{ fontWeight: 650 }}>0 ₽ · Без карты и автопродления</Typography>
                        </Stack>
                    )}
                    {step === 1 && (
                        <Stack spacing={2}>
                            <Alert severity="info">
                                Подтверждение почты и привязка к боту защищают от повторной активации бесплатных лидов.
                            </Alert>
                            <Box sx={{ pb: 2, borderBottom: '1px solid var(--lm-border, #DDE6F0)' }}>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    spacing={1}
                                >
                                    <Typography fontWeight={600}>E-mail</Typography>
                                    <Typography color={status?.email_confirmed ? 'success.main' : 'text.secondary'}>
                                        {status?.email_confirmed ? 'Подтверждён' : 'Нужно подтвердить'}
                                    </Typography>
                                </Stack>
                                <Typography
                                    variant="body2"
                                    sx={{ overflowWrap: 'anywhere' }}
                                >
                                    {email}
                                </Typography>
                                {!status?.email_confirmed && (
                                    <Stack
                                        spacing={1}
                                        sx={{ mt: 1.5 }}
                                    >
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            disabled={busy}
                                            onClick={() =>
                                                void runAction(async () => {
                                                    await refreshEmailCode();
                                                    setNotice('Код отправлен на вашу почту.');
                                                })
                                            }
                                        >
                                            Отправить код
                                        </Button>
                                        <TextField
                                            label="Код из письма"
                                            value={code}
                                            size="small"
                                            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                                            onChange={(event) =>
                                                setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                                            }
                                        />
                                        <Button
                                            type="button"
                                            disabled={busy || code.length !== 6}
                                            onClick={() =>
                                                void runAction(async () => {
                                                    await confirmEmail({ email, code });
                                                    await refresh();
                                                })
                                            }
                                        >
                                            Подтвердить почту
                                        </Button>
                                    </Stack>
                                )}
                            </Box>
                            <Box
                                component="section"
                                aria-labelledby="free-start-telegram-title"
                                sx={{
                                    p: { xs: 2, sm: 2.5 },
                                    border: '1px solid var(--lm-border, #DDE6F0)',
                                    borderRadius: '14px',
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 40,
                                            height: 40,
                                            flexShrink: 0,
                                            borderRadius: '12px',
                                            bgcolor: '#EDF3FF',
                                            color: '#0044FF',
                                        }}
                                    >
                                        <IconBrandTelegram
                                            size={24}
                                            aria-hidden="true"
                                        />
                                    </Box>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        gap={1}
                                        flexWrap="wrap"
                                        sx={{ flex: 1, minWidth: 0 }}
                                    >
                                        <Typography
                                            id="free-start-telegram-title"
                                            fontWeight={600}
                                        >
                                            Telegram-бот
                                        </Typography>
                                        <Box
                                            component="span"
                                            role="status"
                                            aria-live="polite"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: '8px',
                                                bgcolor: status?.telegram_connected ? '#EAF7F0' : '#F0F4F8',
                                                color: status?.telegram_connected ? '#18794E' : '#53677F',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {status?.telegram_connected && (
                                                <IconCircleCheck
                                                    size={15}
                                                    aria-hidden="true"
                                                />
                                            )}
                                            {status?.telegram_connected ? 'Аккаунт привязан' : 'Не привязан'}
                                        </Box>
                                    </Stack>
                                </Stack>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 1.5, mb: 2 }}
                                >
                                    {status?.telegram_connected
                                        ? 'Telegram подключён к вашему аккаунту.'
                                        : 'Нажмите «Старт» в боте и вернитесь сюда.'}
                                </Typography>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1}
                                >
                                    {!status?.telegram_connected && (
                                        <Button
                                            component="a"
                                            variant="contained"
                                            sx={{ ...FREE_START_PRIMARY_SX, px: 2 }}
                                            href={bindLink || undefined}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            disabled={!bindLink || busy}
                                        >
                                            Привязать аккаунт к боту
                                        </Button>
                                    )}
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        startIcon={<IconRefresh size={18} />}
                                        sx={{
                                            minHeight: 48,
                                            borderRadius: '12px',
                                            borderColor: 'var(--lm-border, #DDE6F0)',
                                            color: 'text.secondary',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            '&:hover': { borderColor: '#A9BDD8', bgcolor: 'action.hover' },
                                        }}
                                        disabled={busy}
                                        onClick={() => void refresh()}
                                    >
                                        Проверить привязку
                                    </Button>
                                </Stack>
                            </Box>
                        </Stack>
                    )}
                    {step === 2 && (
                        <Stack spacing={2}>
                            <Typography>Готово к запуску: «{niche}»</Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                На баланс этого потока поступят 15 бесплатных лидов. Найденные заявки появятся в
                                кабинете.
                            </Typography>
                            <Typography variant="body2">Стоимость: 0 ₽. Автопродление отключено.</Typography>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        type="button"
                        onClick={close}
                        disabled={busy || disabled}
                    >
                        Закрыть
                    </Button>
                    {step > 0 && (
                        <Button
                            type="button"
                            disabled={busy || disabled}
                            onClick={() => setStep(step - 1)}
                        >
                            Назад
                        </Button>
                    )}
                    <Button
                        type="button"
                        variant="contained"
                        sx={FREE_START_PRIMARY_SX}
                        disabled={busy || disabled || !status?.available || (step > 0 && !ready)}
                        onClick={() => {
                            if (step < 2) setStep(step + 1);
                            else
                                void runAction(async () => {
                                    await onActivate();
                                    if (mounted.current) setOpen(false);
                                });
                        }}
                    >
                        {busy ? <CircularProgress size={20} /> : step === 2 ? 'Запустить 15 лидов бесплатно' : 'Далее'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
